import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { WebClient } from '@injectables';
import { Client, User } from '@models';
import { DialogService } from '../dialog.service';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { RestTypes } from '@lib/model-meta';
import { RestModel } from '@lib/web-client/rest-model';
import { MeasurementTypeToUnit } from '@lib/angular/measurement/measurement-type-to-unit';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { UnitSystem } from '@lib/measurement/types';
import { measurement } from '@lib/measurement';
import { formatStr } from '@lib/angular/measurement/format-str';
import { MatChipInputEvent } from '@angular/material/chips';
import { asFiniteNumber } from '@lib/number';

@Component({
    selector: 'dialog-add-bench-config',
    templateUrl: './dialog-add-bench-config.component.html',
    styleUrls: ['./dialog-add-bench-config.component.scss']
})

export class DialogAddBenchHeightConfigComponent {
    public selectedClient: Client | null = null;
    public benchValuesArray: number[] = [];
    public restClient: RestModel<Client, RestTypes>;
    public loading = false;
    public newValue: number | null = null;
    public unitStr = '';
    public title: string = '';
    private readonly unitSystem$ = new BehaviorSubject<UnitSystem>('imperialUnit');
    public hasInteracted = false;

    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly webClient: WebClient,
        public readonly user: User,
        private readonly host: MatDialogRef<boolean>,
        protected readonly dialog: DialogService,
        private readonly measurementTypeToUnit: MeasurementTypeToUnit,
    ) {
        this.restClient = this.webClient.model.client;
    }

    ngOnInit() {
        this.loadClientData();
    }

    private loadClientData() {
        this.loading = true;

        combineLatest([this.fragQuery.client$, this.fragQuery.particleSizeUnitSystem$])
            .subscribe(([client, unitSystem]) => {
                if (!client) return this.resetLoading();

                this.selectedClient = client;
                this.unitSystem$.next(unitSystem);
                const typeToUnit = this.measurementTypeToUnit.getUnitsForSystem(unitSystem);
                this.unitStr = measurement.abbreviation(typeToUnit["distance"]);
                this.benchValuesArray = this.parseBenchValues(client.benchHeight, typeToUnit);
                if (this.benchValuesArray.length > 0) {
                    this.hasInteracted = true;
                }
                this.loading = false;
            });
    }

    private parseBenchValues(values: string | null, typeToUnit: any): number[] {
        if (!values?.trim()) return [];

        const parsedValues = values.split(',')
            .map(num => asFiniteNumber(num))
            .filter(num => !isNaN(num));

        return parsedValues.length 
            ? formatStr(parsedValues, "distance", typeToUnit, 0, '${v}', false)
                .split(',')
                .map(asFiniteNumber)
                .filter(val => !isNaN(val))
            : [];
    }

    public valid(): boolean {
        return this.hasInteracted || this.benchValuesArray.length > 0 || (this.newValue !== null && this.newValue > 0);
    }

    public cancel(): void {
        this.host.close(false);
    }

    public async save(): Promise<void> {
        if (this.newValue && this.newValue !== null && this.newValue > 0 && !this.benchValuesArray.includes(this.newValue)) {
            this.benchValuesArray.push(this.newValue);
        }

        if (!this.selectedClient) return;

        const updatedClient = { ...this.selectedClient };
        const benchValues = this.benchValuesArray.map(val => measurement.convertToBaseUnits(val, this.unitStr === 'ft' ?'feet': 'meter'))
        updatedClient['benchHeight'] = benchValues.join(',');

        try {
            await this.dialog.await(this.restClient.save(updatedClient));
            this.fragQuery.trigger$.next();
            this.host.close(true);
        } catch (error) {
            console.error('Save failed', error);
        }
    }

    public removeValue(value: number): void {
        this.benchValuesArray = this.benchValuesArray.filter(v => v !== value);

        if (this.benchValuesArray.length === 0 && this.hasInteracted) {
            return;
        }
    }

    public addValue(event: MatChipInputEvent): void {
        const value = parseFloat(event.value.trim());
        if (!isNaN(value) && !this.benchValuesArray.includes(value)) {
            this.benchValuesArray.push(value);
            this.hasInteracted = true;
        }
        this.newValue = null
        event.chipInput?.clear();
    }

    private resetLoading(): void {
        this.selectedClient = null;
        this.benchValuesArray = [];
        this.loading = false;
    }
}
